/**
 * Reading a device's drawing choice off its `settings`.
 *
 * The hub declares each drawing family as a select setting (`pump_type`,
 * `tank_type`, `cover_style`, `reading_type`, `appliance_type`, `fan_style`, and
 * the older `binary_sensor_subtype`) and stamps it with `display: 'drawing'`. The
 * value is the drawing's own id; `'auto'` means "read the legacy icon and name
 * instead", which is also what a device saved before the setting existed carries.
 *
 * Pure: no Vue, no store, so both the widgets and the editor's variant registry
 * can read it, and the Node test runner can exercise it.
 */

/** The value an object holds for `key`, or `undefined` when it holds none. */
export function readSetting(settings: unknown, key: string): unknown {
  if (!settings || typeof settings !== 'object' || Array.isArray(settings)) return undefined;
  return (settings as Record<string, unknown>)[key];
}

/**
 * The variant a device's settings name, when it is one of `allowed`. Anything
 * else — an absent setting, `'auto'`, a value from an older vocabulary — answers
 * `null` so the caller falls through to the legacy icon/name resolvers.
 */
export function variantFromSetting(settings: unknown, key: string, allowed: readonly string[]): string | null {
  const raw = readSetting(settings, key);
  if (typeof raw !== 'string') return null;
  const value = raw.trim();
  return value !== '' && value !== 'auto' && allowed.includes(value) ? value : null;
}
